/**
 * TrialExpiryBanner — hero banner that fires when the 14-day Pro trial
 * has ≤3 days remaining. One-click conversion with a "TRIAL3DAY20"
 * promotion code (20% off first month) so the user doesn't have to
 * hunt for a coupon field.
 *
 * Design goals:
 *  - High-visibility (top of dashboard, above every widget) but never
 *    intrusive — dismissible for 6h so users see the "1 day left" ping
 *    even after dismissing the "3 days left" one.
 *  - Amber pulse on the CTA matches the existing "Most popular" badge
 *    pulse on the pricing page so the whole conversion loop feels
 *    visually consistent.
 *  - Silent no-op for non-trial users (paid, expired, converted).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, X } from "lucide-react";

const DISMISS_KEY = "bfa_trial_expiry_dismiss_ts";
const DISMISS_TTL_MS = 6 * 60 * 60 * 1000;  // 6 hours
const PROMO_CODE = "TRIAL3DAY20";           // Stripe Promotion Code — 20% off first month

export default function TrialExpiryBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      return Boolean(ts && Date.now() - ts < DISMISS_TTL_MS);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let alive = true;
    api.get("/trial/status").then((r) => alive && setStatus(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  };

  const convert = async () => {
    setBusy(true);
    try {
      const res = await api.post("/billing/checkout-subscription", {
        plan_id: "professional",     // Backend's Pro tier plan_id
        cycle: "monthly",
        origin_url: window.location.origin,
        promo_code: PROMO_CODE,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Couldn't start checkout — please try again");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed to start");
    } finally {
      setBusy(false);
    }
  };

  // Gate: only render for active-trial users with ≤3 days left
  if (dismissed || !status) return null;
  if (status.status !== "active") return null;
  const daysLeft = status.days_remaining;
  if (daysLeft == null || daysLeft > 3) return null;

  const urgencyCopy = daysLeft <= 1
    ? { line: "Your trial ends in less than 24 hours.", cta: "Keep Pro — 20% off first month" }
    : { line: `Your Pro trial ends in ${daysLeft} days.`, cta: "Convert to Pro — 20% off first month" };

  return (
    <div
      data-testid="trial-expiry-banner"
      className="mb-6 rounded-xl border border-brand-amber/50 bg-gradient-to-r from-brand-amber/10 via-brand-amber/5 to-transparent p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber mb-1">
          // {daysLeft <= 1 ? "TRIAL EXPIRING TODAY" : "TRIAL ENDING SOON"}
        </div>
        <div className="text-ink-primary text-base font-medium">
          {urgencyCopy.line}{" "}
          <span className="text-ink-secondary font-normal">
            Convert now to keep all Pro studios, 1,500 AI generations, BYOK access, and priority support — with 20% off your first month.
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={convert}
          disabled={busy}
          data-testid="trial-expiry-convert-btn"
          className="popular-pulse inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {busy ? "Starting…" : urgencyCopy.cta}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="trial-expiry-dismiss"
          className="p-2 rounded-md text-ink-muted hover:text-ink-primary hover:bg-bg-elevated transition"
          title="Hide for 6 hours"
          aria-label="Dismiss trial expiry banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
